// The filter panel's "Custom fields" section: a heading with a rule, one tile per custom field available on
// the list (A-Z by name as the admin typed it), and the drawer of the one open field below all the tiles.
// A tile is the panel's own closed dropdown: same box, text and caret as the filters beside it. While its
// field has values applied, or its drawer is open, it takes the accent border, with the count, "(3)" or
// "Not (3)", in the accent colour. Closing a drawer, by its tile or by opening another, applies a term typed
// into it but not added.
import React, {useContext, useState} from 'react';
import {useIntl} from 'react-intl';
import Styled from 'styled-components';
import type {CustomFieldColumnDef} from '../custom-field-columns/customFieldColumnModel';
import {DropdownGrid, SmallTitle} from '../filters/filter.styled';
import {
	Container,
	DropdownButton,
	DropdownButtonPlaceholder,
	IconContainer,
} from '../../../../web-components/core/dropdown/dropdown.styled';
import {CaretIcon} from '../../../../web-components/icons/caret-icon';
import {CustomFieldDraftContext} from './customFieldFilterDrafts';
import {customFieldFilterKey} from './customFieldFilterKeys';
import {customFieldCountLabel, type CustomFieldFilterFormat, type FormatMessage} from './customFieldFilterLabels';
import {normaliseValues} from './customFieldFilterSelection';
import {PANEL_ACCENT, PANEL_FONT_SIZE, PANEL_HEADING_TEXT, PANEL_TEXT, isPressKey} from './customFieldFilter.styled';
import {CustomFieldFilterDrawer} from './CustomFieldFilterDrawer';

// The panel's small uppercase label at the size of a section heading, followed by a rule to the edge.
const Heading = Styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	margin: 4px 0 12px;

	&::after {
		content: '';
		flex: 1;
		border-top: 1px solid #ebebeb;
	}
`;

const HeadingText = Styled(SmallTitle)`
	font-size: ${PANEL_FONT_SIZE};
	font-weight: 500;
	letter-spacing: 0.06em;
	color: ${PANEL_HEADING_TEXT};
`;

// Tiles sized to their names, growing to fill each row.
const FlowTiles = Styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
	margin-bottom: 14px;

	/* Doubled to outrank the tile's own full width, which the native dropdown it reuses sets. */
	&& > * {
		flex: 1 1 auto;
		width: auto;
		min-width: 148px;
		max-width: 100%;
	}
`;

// The panel's own three columns, as its native filters sit in.
const GridTiles = Styled(DropdownGrid)`
	& > * {
		min-width: 0;
	}
`;

// The closed dropdown keeps its rounded corners while highlighted; it opens no menu of its own.
const Tile = Styled(Container)`
	&& {
		border-radius: 4px;
	}

	&:focus-visible {
		outline: none;
		border-color: ${PANEL_ACCENT};
	}
`;

const TileLabel = Styled(DropdownButtonPlaceholder)`
	flex: 1 1 auto;
	text-overflow: ellipsis;
`;

const TileCount = Styled.span`
	flex: none;
	margin-left: 6px;
	white-space: nowrap;
	color: ${PANEL_ACCENT};
`;

export interface CustomFieldFilterSectionProps {
	definitions: CustomFieldColumnDef[];
	selection: Record<string, unknown>;
	excluded: (filterType: string) => boolean;
	format: CustomFieldFilterFormat;
	onReplace: (filterType: string, values: unknown[]) => void;
	onExcludeChange: (filterType: string, checked: boolean) => void;
	allowExclude: boolean;
	/** 'flow' sizes the tiles to their names; 'grid' puts them in the panel's three equal columns. */
	layout?: 'flow' | 'grid';
}

export const CustomFieldFilterSection = ({
	definitions,
	selection,
	excluded,
	format,
	onReplace,
	onExcludeChange,
	allowExclude,
	layout = 'flow',
}: CustomFieldFilterSectionProps): JSX.Element | null => {
	const intl = useIntl();
	const say: FormatMessage = (descriptor, values) => intl.formatMessage(descriptor, values) as string;
	const registry = useContext(CustomFieldDraftContext);
	const [openId, setOpenId] = useState<string | null>(null);
	if (definitions.length === 0) return null;

	const valuesOf = (definition: CustomFieldColumnDef): unknown[] => {
		const stored = selection[customFieldFilterKey(definition.id)];
		return normaliseValues(definition, Array.isArray(stored) ? stored : []);
	};
	const toggle = (definitionId: string) => {
		registry?.collect().forEach(draft => onReplace(draft.filterType, draft.values));
		setOpenId(current => (current === definitionId ? null : definitionId));
	};
	const open = definitions.find(definition => definition.id === openId);
	const Tiles = layout === 'grid' ? GridTiles : FlowTiles;

	return (
		<div data-cy="cf-filter-section">
			<Heading>
				<HeadingText>{say({id: 'settings.custom_fields.title'})}</HeadingText>
			</Heading>
			<Tiles data-cy="cf-filter-tiles" data-layout={layout}>
				{definitions.map(definition => {
					const count = valuesOf(definition).length;
					const isOpen = definition.id === openId;
					const countLabel = customFieldCountLabel(count, excluded(customFieldFilterKey(definition.id)), say);
					return (
						<Tile
							key={definition.id}
							role="button"
							tabIndex={0}
							title={definition.displayName}
							data-cy={`cf-filter-tile-${definition.key}`}
							data-userpilot="filter-dropdown"
							data-applied={count > 0}
							aria-expanded={isOpen}
							expanded={isOpen || count > 0}
							onClick={() => toggle(definition.id)}
							onKeyDown={(event: React.KeyboardEvent) => {
								if (!isPressKey(event.key)) return;
								event.preventDefault();
								toggle(definition.id);
							}}
						>
							<DropdownButton>
								<TileLabel>{definition.displayName}</TileLabel>
								{countLabel && <TileCount>{countLabel}</TileCount>}
								<IconContainer>
									<CaretIcon
										color={PANEL_TEXT}
										direction={isOpen ? CaretIcon.DIRECTION.UP : CaretIcon.DIRECTION.DOWN}
									/>
								</IconContainer>
							</DropdownButton>
						</Tile>
					);
				})}
			</Tiles>
			{open && (
				<CustomFieldFilterDrawer
					key={open.id}
					definition={open}
					values={valuesOf(open)}
					excluded={excluded(customFieldFilterKey(open.id))}
					allowExclude={allowExclude}
					format={format}
					onReplace={values => onReplace(customFieldFilterKey(open.id), values)}
					onExcludeChange={checked => onExcludeChange(customFieldFilterKey(open.id), checked)}
				/>
			)}
		</div>
	);
};
